import { useCallback, useMemo } from 'react';

import { window } from '../../../../globals';
import useAsyncRequest from '../../../../hooks/useAsyncRequest';
import useAsyncValue from '../../../../hooks/useAsyncValue';
import useRequestConnectedToAsyncValue from '../../../../hooks/useRequestConnectedToAsyncValue';
import { get, remove } from '../../../../redux/api';
import { hardRedirectTo, nuc, selectOptionFactory } from '../../../../utils';
import { useProjectSettingsContext } from '../context';
import { useCanUserEditProject } from '../permissions';
import type { ProjectLabel, SelectOption } from '../../../../types';

import type { ProjectGeneralSettings } from './types';

function fetchLabels() {
  return get<{ labels: ProjectLabel[] }>('/api/organizations/labels');
}

function deleteProject(locator: string) {
  return remove<void, void>(`/api/projects/${nuc(locator)}`)
    .then(() => hardRedirectTo('/projects')) as Promise<{ data?: null }>;
}

//  Lis of all the VCS hosts we support.
const vcsHosts: SelectOption[] = [
  selectOptionFactory('github', 'GitHub'),
  selectOptionFactory('github-app', 'GitHub App'),
  selectOptionFactory('bitbucket_cloud', 'Bitbucket Cloud'),
  selectOptionFactory('gitlab', 'GitLab'),
  selectOptionFactory('azure_repos', 'Azure Repos'),
].concat(
  //  Bitbucket Server is only available on-prem.
  window.on_prem ? [selectOptionFactory('bitbucket_server', 'Bitbucket Server')] : [],
);

const integrationHookFailStates: Array<SelectOption<ProjectGeneralSettings['integrationhook_fail_state']>> = [
  selectOptionFactory('SUCCESS', 'Show a success status'),
  selectOptionFactory('ERROR', 'Show a failure status'),
];

/**
 * This is the hook for the general settings page.
 * It pulls in the editable fields from the project context,
 * along with helpers for updating and saving the data.
 *
 * It also fetches other data which the page depends on and provides
 * it in the return value. General settings depends on:
 * - Project labels (`/api/organizations/labels`)
 */
const useProjectGeneralSettings = () => {
  const { general } = useProjectSettingsContext();
  const labelsAsyncValue = useAsyncRequest(fetchLabels);
  const deleteProjectAsyncValue = useAsyncValue<null>();
  const deleteProjectForThisLocator = useCallback(() => deleteProject(general.locator), [general.locator]);
  const onDeleteProject = useRequestConnectedToAsyncValue(deleteProjectForThisLocator, deleteProjectAsyncValue);
  const canEdit = useCanUserEditProject(general.locator);

  return {
    locator: general.locator,
    isSaving: general.isSaving,
    isLoading: labelsAsyncValue.isLoading,
    isDirty: general.isDirty,
    onUpdate: general.onUpdate,
    onSave: general.onSave,
    onDeleteProject,
    canEdit,
    isSaveButtonDisabled: !canEdit ||
      !general.isDirty ||
      general.isSaving ||
      deleteProjectAsyncValue.isLoading ||
      labelsAsyncValue.isLoading,
    //  Map parts of the settings which should be passed to the UI as SelectOptions
    settings: useMemo(() => ({
      ...general.settings,
      labels: general.settings.labels?.map(label => selectOptionFactory(label.id, label.label)) ?? [],
      default_branch: general.settings.default_branch
        ? selectOptionFactory(general.settings.default_branch)
        //  We always default to master if nothing is passed.
        : undefined,
      vcs_host: general.settings.vcs_host
        ? vcsHosts.find(host => host.value === general.settings.vcs_host)
        //  We always default to master if nothing is passed.
        : undefined,
      tracking_branches: general.settings.tracking_branches?.map(branch => selectOptionFactory(branch)) ?? [],
      transitive_excludes: general.settings.transitive_excludes?.map(exclude => selectOptionFactory(exclude)) ?? [],
      integrationhook_fail_state: general.settings.integrationhook_fail_state
        ? integrationHookFailStates.find(state => state.value === general.settings.integrationhook_fail_state)
        : undefined,
    }), [general.settings]),
    //  This is ALL the options for branches in the default branch dropdown and tracking branches picker.
    branches: useMemo(() => general.settings.references?.filter(ref => ref.type === 'branch')
      .map(ref => selectOptionFactory(ref.name)) ?? [], [general.settings.references]),
    //  This is the list of ALL organization labels to populate the dropdown.
    //  Keep labels stable across renders, since they never change.
    labels: useMemo(() => {
      return labelsAsyncValue.value?.labels.map(label => selectOptionFactory(label.id, label.label)) ?? [];
    }, [labelsAsyncValue.value]),
    vcsHosts,
    integrationHookFailStates,
  };
};

export default useProjectGeneralSettings;
